<template>
  <div class="component-wrap">
    <AppContentHeader headline="Materials dashboard">
      <div class="content-header__actions">
        <router-link
          tag="md-button"
          to="/material/materials"
          class="md-primary md-raised"
          >Manage materials
        </router-link>

        <md-button class="md-primary md-raised" @click="downloadPrintTickets()"
          >Download print tickets
        </md-button>

        <md-button class="md-primary md-raised" @click="excelExport()"
          >Excel Export
        </md-button>

        <md-button
          class="md-primary md-raised"
          :disabled="uploadingScannedPdf"
          @click="openUploadDialog"
        >
          {{ uploadingScannedPdf ? "Uploading..." : "Upload" }}
        </md-button>

        <input
          ref="scannedPdfInput"
          type="file"
          accept=".pdf,application/pdf"
          @change="onScannedPdfSelected"
          style="display: none"
        />
      </div>
    </AppContentHeader>

    <div class="main-hdr">
      <material-dashboard-search
        ref="materialSearch"
        :adspaces-description.sync="searchFilter.adspaces_description"
        @updateFilterResults="updateSearchFilters"
        @setResultsOrders="setResultsOrders"
        @searchInput="inputSearch"
        @loadingSearchResults="handleSearchLoading"
      />
    </div>

    <div>
      <AppSpinner
        v-if="loadingSpinner || searchLoading"
        :loading="loadingSpinner || searchLoading"
      >
        <template v-slot:default>
          <div style="text-align:center; margin-top:16px; font-size:1.2em;">
            Loading...
          </div>
        </template>
      </AppSpinner>
      <material-dashboard-table
        ref="matDashboard"
        v-if="filteredOrders.length && !loadingSpinner && !searchLoading"
        :searchStr="searchString"
        :orderType="orderType"
        :materials-prop="filteredOrders"
        @getAllOrders="emitSearchFunction"
        @searchInput="inputSearch"
        @reOrder="reOrderColumn"
      ></material-dashboard-table>
      <AppEmptyState
        message="There are no orders to display."
        v-if="!filteredOrders.length && !loadingSpinner && !searchLoading"
      />
    </div>

    <md-snackbar
      md-position="center"
      :md-active.sync="snackbar.show"
      :md-duration="snackbar.duration"
      md-persistent
    >
      <span>{{ snackbar.message }}</span>
      <md-button class="md-primary" @click="snackbar.show = false"
        >Got it</md-button
      >
    </md-snackbar>
    <!-- snackbar to show response message -->
  </div>
</template>

<script>
import MaterialDashboardTable from "../../components/MaterialDashboard/MaterialDashboardTable.vue";
import MaterialDashboardSearch from "../../components/MaterialDashboard/MaterialDashboardSearch";
import { DeepFilterMixin } from "@/mixins/DeepFilterMixin";
import { InitializeOrderMixin } from "@/mixins/InitializeOrderMixin";
import Cookie from "js-cookie";

export default {
  name: "MaterialDashboard",
  mixins: [DeepFilterMixin, InitializeOrderMixin],
  data: function() {
    return {
      orders: [],
      snackbar: {
        show: false,
        message: "",
        duration: 4000,
      },
      orderType: "",
      itemsToShow: 9,
      scrollPosition: 0,
      ordersToDisplay: [],
      loadingSpinner: true,
      filter: "",
      previousSeason: false,
      showAllOrders: false,
      filteredOrders: [],
      searchString: "",
      initialLoad: true,
      searchFilter: {
        search: "",
        media_type: "sponsorship",
        size: "",
        status_id: "",
        material_id: "",
        adspaces_description: "",
      },
      materials: [],
      selectedOrder: null,
      searchLoading: false,
      uploadingScannedPdf: false,
    };
  },
  components: {
    MaterialDashboardSearch,
    MaterialDashboardTable,
  },
  created() {
    this.loadData().then(() => {
      this.filter = this.$store.getters["getMaterialFilter"];
      this.initialLoad = false;
    });
  },
  mounted() {
    const q = this.$route.query.adspaces_description;
    if (q != null && q !== "") {
      this.searchFilter.adspaces_description = String(q);
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch("saveMaterialFilter", this.filter);
    next();
  },
  watch: {
    showAllOrders(value) {
      // this.orders = [];
      let requestString = {};

      if (value) {
        requestString = `/unset-material-orders?season_id=${Cookie.get(
          "season_id"
        )}`;
      } else {
        requestString = `/unset-material-orders?ready_to_print=true?season_id=${Cookie.get(
          "season_id"
        )}`;
      }

      this.getOrders(requestString);
    },
  },
  methods: {
    openUploadDialog() {
      if (this.uploadingScannedPdf) {
        return;
      }
      if (this.$refs.scannedPdfInput) {
        this.$refs.scannedPdfInput.value = "";
        this.$refs.scannedPdfInput.click();
      }
    },

    async onScannedPdfSelected(event) {
      const file =
        event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;

      if (!file) {
        return;
      }

      const hasPdfMime = file.type === "application/pdf";
      const hasPdfExtension = /\.pdf$/i.test(file.name || "");

      if (!hasPdfMime && !hasPdfExtension) {
        this.showSnackbar("Please select a single PDF file.");
        return;
      }

      await this.uploadScannedPdf(file);
    },

    async uploadScannedPdf(file) {
      this.uploadingScannedPdf = true;
      this.loadingSpinner = true;

      const payload = new FormData();
      payload.append("uploaded_file", file, file.name);

      try {
        const seasonId = Cookie.get("season_id");
        if (!seasonId) {
          this.showSnackbar("Active season is not selected.");
          return;
        }

        const response = await this.$axios.post(
          `/sponsorships/${seasonId}/print-ticket/match`,
          payload
        );
        this.applyUploadResponse(response && response.data ? response.data : {});
      } catch (err) {
        const message =
          err && err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "We couldn't upload this PDF.";
        this.showSnackbar(message);
      } finally {
        this.uploadingScannedPdf = false;
        this.loadingSpinner = false;
      }
    },

    applyUploadResponse(payload) {
      const totalOrders = Number(payload.total_orders || 0);
      const verifiedOrders = Number(payload.verified_orders || 0);
      const responseOrders = Array.isArray(payload.orders) ? payload.orders : [];
      const verifiedIds = responseOrders
        .filter((order) => order && order.verified && order.id != null && order.id !== "")
        .map((order) => String(order.id));

      this.filterOrdersByVerifiedIds(verifiedIds);

      if (totalOrders > 0 && totalOrders === verifiedOrders) {
        this.showSnackbar(`All ${verifiedOrders} orders were verified.`);
        return;
      }

      const issues = responseOrders
        .filter((order) => !order || !order.verified)
        .map((order) => {
          const page = order && order.page ? order.page : "?";

          if (!order || order.id == null || order.id === "") {
            return `page ${page}: can't read sponsorship id`;
          }

          return `page ${page}: can't verify sponsorship ${order.id}`;
        });

      const summary = `Verified ${verifiedOrders}/${totalOrders}.`;
      if (!issues.length) {
        this.showSnackbar(summary);
        return;
      }

      this.showSnackbar(`${summary} Missing parts: ${issues.join("; ")}`);
    },

    filterOrdersByVerifiedIds(verifiedIds) {
      if (!Array.isArray(verifiedIds) || verifiedIds.length === 0) {
        this.orders = [];
        this.filteredOrders = [];
        this.selectedOrder = null;
        this.orderType = "";
        return;
      }

      const matchedOrders = this.orders.filter((order) =>
        verifiedIds.includes(String(order.id))
      );

      this.orders = matchedOrders;
      this.filteredOrders = matchedOrders;
      this.orderType = matchedOrders.length ? matchedOrders[0].mediaType : "";
      this.selectedOrder = matchedOrders.length ? matchedOrders[0] : null;
    },

    async excelExport() {
       let data = {params: {...this.searchFilter, previous_season: this.previousSeason}};

            if(this.orderType === 'signage'){
                this.$axios.get(`signage-orders/export?season_id=${Cookie.get('season_id')}`, data).then(response => {
                    console.log('Done')
                    window.open(response.data, '_blank');

                }).catch(() => {
                    this.showSnackbar('We couldnt export the signage table.');
                });
            }
            else{
                this.$axios.get(`sponsorships/export?season_id=${Cookie.get('season_id')}`, data).then(response => {
                    console.log('Done')
                    window.open(response.data, '_blank');
                }).catch(() => {
                    this.showSnackbar('We couldnt export the sponsorships table.');
                });
            }
    },

    updateSearchFilters(filtersObj) {
      const adspacesDescription = this.searchFilter.adspaces_description;
      this.searchFilter = { ...filtersObj.filters };
      this.searchFilter.adspaces_description = adspacesDescription;
      this.previousSeason = filtersObj.season;
    },

    downloadPrintTickets() {
      if (this.$refs.matDashboard) {
        this.$refs.matDashboard.getAllChecked();
      } else {
        this.showSnackbar("There are no orders yet.");
      }
    },

    emitSearchFunction() {
      this.$refs.materialSearch.search(false);
    },
    reOrderColumn(fieldToReorder) {
      this.filteredOrders.sort((a, b) =>
        a[fieldToReorder] > b[fieldToReorder] ? 1 : -1
      );
    },

    inputSearch(searchStr) {
      this.searchLoading = true;
      let _this = this;
      this.searchString = searchStr;
      setTimeout(function() {
        _this.filteredOrders = _this.orders.filter((x) =>
          _this.filterFunction(x, searchStr)
        );
        _this.searchLoading = false;
      }, 1200);
    },

    setResultsOrders(orders) {
      if (!orders.length) {
        // Clear table when no results
        this.orders = [];
        this.selectedOrder = null;
        this.filteredOrders = [];
        this.orderType = "";
        if (this.initialLoad === false && this.$refs.matDashboard) {
          this.$refs.matDashboard.updateOrders([], "");
        }
        return;
      }

      let tempOrders = [];
      orders = orders.filter((m) => m !== undefined);
      orders.forEach((el) => tempOrders.push(this.initializeOrder(el)));

      this.orders = tempOrders.filter((m) => m !== undefined);
      this.filteredOrders = tempOrders.filter((m) => m !== undefined);

      this.orderType = this.orders[0].mediaType;
      this.selectedOrder = this.orders[0]; // Default to first order

      if (this.initialLoad === false && this.$refs.matDashboard) {
        this.$refs.matDashboard.updateOrders(
          this.filteredOrders,
          this.orderType
        );
      }
    },

    setSelectedOrder(order) {
      this.selectedOrder = order;
    },

    showSnackbar(errorMessage = null) {
      this.snackbar.message = errorMessage
        ? errorMessage
        : "Material successfully set";
      this.snackbar.show = true;
    },

    async getOrders() {
      this.loadingSpinner = true;
      try {
        const sponsorships = (
          await this.$axios.get(
            `/sponsorships?season_id=${Cookie.get("season_id")}&media_id=6`
          )
        ).data
          // Exclude items with specific sponsorship_status_name values
          .filter((x) => ![
            "No Artwork Needed"
          ].includes(x.sponsorship_status_name))
          .map((x) => {
            return {
              ...x,
              index: x.sponsorships_id,
            };
          });

        if (sponsorships.length === 0) {
          this.snackbar.message = "There are no orders yet.";
          this.snackbar.show = true;
        } else {
          this.setResultsOrders([...sponsorships]);
        }

        this.loadingSpinner = false;
      } catch (err) {
        this.loadingSpinner = false;
        this.snackbar.message = err.message;
        this.snackbar.show = true;
      }
    },

    async loadData() {
      await this.getMaterials();
      await this.getOrders();
    },

    getMaterials() {
      return this.$store.dispatch("materials/getMaterials");
    },

    updateMaterials(newMaterials) {
      this.materials = newMaterials;
    },
    handleSearchLoading(isLoading) {
      this.searchLoading = isLoading;
    },
  },
};
</script>

<style lang="scss" scoped>
@include scroller-styles;

.toggle-label {
  margin-right: 1.2rem !important;
  font-size: 1.5rem;
  font-weight: bold;
}

.main-hdr {
  padding-top: 33px;
}
</style>
